import { useEffect, useState } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Zap, Droplet, Trash2, Cloud, AlertTriangle, Lightbulb, Info, CheckCircle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { generateESGReport } from '@/utils/generateESGReport';
import { useCompanySettings } from '@/context/CompanySettingsContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const { companyName, industry, logoUrl } = useCompanySettings();

  useEffect(() => {
    fetchDashboardData();
    fetchInsights();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard-data`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    try {
      const response = await axios.get(`${API}/insights`);
      setInsights(response.data);
    } catch (error) {
      console.error('Error fetching insights:', error);
    }
  };

  const handleDownloadReport = async () => {
    setGeneratingPDF(true);
    try {
      // Fetch all required data for the report with industry-specific benchmarks
      const [benchmarksRes, risksRes, targetsRes] = await Promise.all([
        axios.get(`${API}/benchmarking?industry=${industry}`),
        axios.get(`${API}/esg-risks`),
        axios.get(`${API}/emission-targets`)
      ]);

      await generateESGReport(
        dashboardData,
        benchmarksRes.data,
        risksRes.data,
        targetsRes.data,
        { companyName, industry, logoUrl }
      );

      toast.success('ESG Report Downloaded', {
        description: `Report generated for ${companyName}`
      });
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A362D]"></div>
      </div>
    );
  }

  const scopeData = dashboardData?.scope_emissions
    ? [
        { name: 'Scope 1', value: dashboardData.scope_emissions['Scope 1'], color: '#C85A32' },
        { name: 'Scope 2', value: dashboardData.scope_emissions['Scope 2'], color: '#7898A5' },
        { name: 'Scope 3', value: dashboardData.scope_emissions['Scope 3'], color: '#8B9D8F' }
      ]
    : [];

  const getInsightIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="w-5 h-5" />;
      case 'recommendation': return <Lightbulb className="w-5 h-5" />;
      case 'alert': return <AlertTriangle className="w-5 h-5" />;
      case 'success': return <CheckCircle className="w-5 h-5" />;
      default: return <Info className="w-5 h-5" />;
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'warning': return 'bg-[#C85A32]/10 border-[#C85A32]/30 text-[#C85A32]';
      case 'alert': return 'bg-[#A33B3B]/10 border-[#A33B3B]/30 text-[#A33B3B]';
      case 'success': return 'bg-[#2D4A3E]/10 border-[#2D4A3E]/30 text-[#2D4A3E]';
      default: return 'bg-[#7898A5]/10 border-[#7898A5]/30 text-[#7898A5]';
    }
  };

  return (
    <div className="p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-[#111814] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            ESG <span className="text-[#C85A32]">VANITIX</span> Dashboard
          </h1>
          <p className="text-base text-[#4B5563] leading-relaxed">Your environmental, social, and governance intelligence hub</p>
        </div>
        <Button
          onClick={handleDownloadReport}
          disabled={generatingPDF}
          className="bg-[#1A362D] hover:bg-[#2D4A3E] text-white flex items-center gap-2"
          data-testid="download-report-button"
        >
          {generatingPDF ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Generating...
            </>
          ) : (
            <>
              <FileText className="w-4 h-4" />
              Download ESG Report
            </>
          )}
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl border border-[#E5E1D8] shadow-sm" data-testid="total-emissions-card">
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 bg-[#C85A32]/10 rounded-lg flex items-center justify-center">
              <Cloud className="w-6 h-6 text-[#C85A32]" />
            </div>
            <div className="flex items-center gap-1 text-[#A33B3B]">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs font-semibold">+5%</span>
            </div>
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-1">Total Emissions</p>
          <p className="text-3xl font-bold text-[#111814]">{dashboardData?.total_emissions.toLocaleString()}</p>
          <p className="text-sm text-[#6B7280] mt-1">kg CO₂e</p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-[#E5E1D8] shadow-sm" data-testid="energy-consumption-card">
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 bg-[#7898A5]/10 rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-[#7898A5]" />
            </div>
            <div className="flex items-center gap-1 text-[#2D4A3E]">
              <TrendingDown className="w-4 h-4" />
              <span className="text-xs font-semibold">-2%</span>
            </div>
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-1">Energy</p>
          <p className="text-3xl font-bold text-[#111814]">{dashboardData?.energy_consumption.toLocaleString()}</p>
          <p className="text-sm text-[#6B7280] mt-1">kWh</p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-[#E5E1D8] shadow-sm" data-testid="water-usage-card">
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 bg-[#7898A5]/10 rounded-lg flex items-center justify-center">
              <Droplet className="w-6 h-6 text-[#7898A5]" />
            </div>
            <div className="flex items-center gap-1 text-[#2D4A3E]">
              <TrendingDown className="w-4 h-4" />
              <span className="text-xs font-semibold">-3%</span>
            </div>
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-1">Water Usage</p>
          <p className="text-3xl font-bold text-[#111814]">{dashboardData?.water_usage.toLocaleString()}</p>
          <p className="text-sm text-[#6B7280] mt-1">Liters</p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-[#E5E1D8] shadow-sm" data-testid="waste-generation-card">
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 bg-[#8B9D8F]/10 rounded-lg flex items-center justify-center">
              <Trash2 className="w-6 h-6 text-[#8B9D8F]" />
            </div>
            <div className="flex items-center gap-1 text-[#2D4A3E]">
              <TrendingDown className="w-4 h-4" />
              <span className="text-xs font-semibold">-8%</span>
            </div>
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-1">Waste</p>
          <p className="text-3xl font-bold text-[#111814]">{dashboardData?.waste_generation.toLocaleString()}</p>
          <p className="text-sm text-[#6B7280] mt-1">kg</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Scope Emissions Pie Chart */}
        <div className="bg-white p-6 rounded-xl border border-[#E5E1D8] shadow-sm" data-testid="scope-emissions-chart">
          <h3 className="text-xl font-semibold tracking-tight text-[#111814] mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Emissions by Scope
          </h3>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={scopeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="name"
                >
                  {scopeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} stroke={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [`${value.toLocaleString()} kg CO₂e`, '']}
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E1D8',
                    borderRadius: '8px',
                    padding: '8px 12px'
                  }}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value) => <span style={{ color: '#4B5563', fontSize: '12px' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 space-y-2">
            {scopeData.map((item) => (
              <div key={item.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-[#4B5563]">{item.name}</span>
                </div>
                <span className="font-semibold text-[#111814]">{item.value?.toLocaleString()} kg</span>
              </div>
            ))}
          </div>
        </div>

        {/* Maturity Score */}
        <div className="bg-white p-6 rounded-xl border border-[#E5E1D8] shadow-sm" data-testid="maturity-score-card">
          <h3 className="text-xl font-semibold tracking-tight text-[#111814] mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            ESG Maturity Score
          </h3>
          <div className="flex flex-col items-center justify-center py-6">
            <div className="relative w-32 h-32">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="#E5E1D8"
                  strokeWidth="12"
                  fill="none"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="#1A362D"
                  strokeWidth="12"
                  fill="none"
                  strokeDasharray={`${(dashboardData?.maturity_score / 5) * 352} 352`}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-4xl font-bold text-[#1A362D]">{dashboardData?.maturity_score}</p>
                  <p className="text-xs text-[#6B7280]">/ 5</p>
                </div>
              </div>
            </div>
            <p className="text-lg font-semibold text-[#111814] mt-4">{dashboardData?.maturity_level}</p>
            <p className="text-sm text-[#6B7280] text-center mt-2 leading-relaxed">
              Your ESG data maturity is at {dashboardData?.maturity_level.toLowerCase()} level
            </p>
          </div>
        </div>

        {/* Monthly Trends */}
        <div className="bg-white p-6 rounded-xl border border-[#E5E1D8] shadow-sm" data-testid="monthly-trends-chart">
          <h3 className="text-xl font-semibold tracking-tight text-[#111814] mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Monthly Emissions
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={dashboardData?.monthly_trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F2F0EB" />
              <XAxis dataKey="month" stroke="#6B7280" style={{ fontSize: '12px' }} />
              <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E1D8',
                  borderRadius: '8px',
                  padding: '8px'
                }}
              />
              <Line type="monotone" dataKey="emissions" stroke="#C85A32" strokeWidth={2} dot={{ fill: '#C85A32', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* AI Insights Panel */}
      <div className="bg-gradient-to-br from-[#C85A32]/5 to-[#7898A5]/5 p-6 rounded-xl border-t-2 border-[#C85A32]" data-testid="ai-insights-panel">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="w-6 h-6 text-[#C85A32]" />
          <h3 className="text-xl font-semibold tracking-tight text-[#111814]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            AI Insights & Recommendations
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {insights.slice(0, 4).map((insight) => (
            <div
              key={insight.id}
              className={`bg-white p-4 rounded-lg border ${
                insight.priority === 'high' ? 'border-[#C85A32]' : 'border-[#E5E1D8]'
              } transition-all duration-200 hover:shadow-sm`}
              data-testid={`insight-${insight.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${getInsightColor(insight.type)}`}>
                  {getInsightIcon(insight.type)}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-[#111814] mb-1">{insight.title}</h4>
                  <p className="text-sm text-[#4B5563] leading-relaxed">{insight.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
